import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Save, FileText } from 'lucide-react';

export const AuditHeaderAdminPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    document_number: 'Z04-S11',
    document_title: 'Interní inspekce',
    issue_date: '1. 12. 2023',
    version: '2',
    effective_from: '1. 1. 2024',
    revision: '03',
    revision_date: '15.12.2025'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/audit-header-settings');
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/audit-header-settings', settings);
      toast.success('Nastavení uloženo');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Chyba při ukládání nastavení');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-3xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/admin')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět na administraci
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-7 h-7 text-emerald-600" />
            Hlavička interní inspekce
          </h1>
          <p className="text-slate-500 mt-1">
            Nastavení globální hlavičky pro všechny interní inspekce
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Údaje dokumentu</CardTitle>
            <CardDescription>
              Tyto údaje se zobrazí v hlavičce každé interní inspekce
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="document_number">Číslo dokumentu</Label>
                <Input
                  id="document_number"
                  value={settings.document_number}
                  onChange={(e) => setSettings({ ...settings, document_number: e.target.value })}
                  placeholder="např. Z04-S11"
                />
              </div>
              <div>
                <Label htmlFor="document_title">Název dokumentu</Label>
                <Input
                  id="document_title"
                  value={settings.document_title}
                  onChange={(e) => setSettings({ ...settings, document_title: e.target.value })}
                  placeholder="např. Interní inspekce"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="issue_date">Datum vydání</Label>
                <Input
                  id="issue_date"
                  value={settings.issue_date}
                  onChange={(e) => setSettings({ ...settings, issue_date: e.target.value })}
                  placeholder="např. 1. 12. 2023"
                />
              </div>
              <div>
                <Label htmlFor="version">Verze</Label>
                <Input
                  id="version"
                  value={settings.version}
                  onChange={(e) => setSettings({ ...settings, version: e.target.value })}
                  placeholder="např. 2"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="effective_from">Účinný od</Label>
                <Input
                  id="effective_from"
                  value={settings.effective_from}
                  onChange={(e) => setSettings({ ...settings, effective_from: e.target.value })}
                  placeholder="např. 1. 1. 2024"
                />
              </div>
              <div>
                <Label htmlFor="revision">Revize</Label>
                <Input
                  id="revision"
                  value={settings.revision}
                  onChange={(e) => setSettings({ ...settings, revision: e.target.value })}
                  placeholder="např. 03"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="revision_date">Datum revize</Label>
              <Input
                id="revision_date"
                value={settings.revision_date}
                onChange={(e) => setSettings({ ...settings, revision_date: e.target.value })}
                placeholder="např. 15.12.2025"
                className="max-w-xs"
              />
            </div>

            {/* Preview */}
            <div className="mt-6 pt-6 border-t">
              <h3 className="font-medium text-slate-800 mb-3">Náhled hlavičky</h3>
              <div className="bg-slate-50 rounded-lg p-4 border">
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div>
                    <h4 className="font-bold text-slate-800">
                      {settings.document_number} {settings.document_title}
                    </h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Verze: {settings.version} | Revize: {settings.revision} | 
                      Účinný od: {settings.effective_from}
                    </p>
                  </div>
                  <div className="text-right text-sm text-slate-600">
                    <p>Datum vydání: {settings.issue_date}</p>
                    <p>Datum revize: {settings.revision_date}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end mt-6">
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Ukládání...' : 'Uložit nastavení'}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default AuditHeaderAdminPage;
